import React, { useState, useEffect } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Stack,
  ButtonGroup,
  Text,
  Tooltip,
  Icon,
  ChoiceList,
  Toast,
  Checkbox,
  RadioButton,
  Banner
} from '@shopify/polaris';
import {
  QuestionMarkMinor,
  SaveMinor,
  RefreshMinor
} from '@shopify/polaris-icons';
import { UserSettingsService } from '../../services/userSettingsService';

export default function InventoryTools({
  selectedProductIds,
  products,
  onInventoryUpdate,
  isInventoryUpdating,
  editHistory,
  user
}) {
  const [inventoryTools, setInventoryTools] = useState({
    inventory_quantity: '',
    inventory_quantity_min: '',
    inventory_quantity_max: '',
    useRange: false,
    ignoreZero: ['yes'], // Default to yes
    trackQuantity: 'unchanged', // 'unchanged' | 'yes' | 'no'
    continueSelling: 'unchanged', // 'unchanged' | 'yes' | 'no'
    chargeTaxes: 'unchanged' // 'unchanged' | 'yes' | 'no'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);

  // Load saved inventory tools settings on component mount
  useEffect(() => {
    console.log('InventoryTools component mounted, user:', user);
    if (user?.uid) {
      loadInventorySettings();
    }
  }, [user]);

  const loadInventorySettings = async () => {
    console.log('loadInventorySettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to load settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      const savedSettings = await UserSettingsService.getUserSettings(user.uid, 'inventory');
      if (savedSettings) {
        // Ensure ignoreZero is an array
        const updatedSettings = {
          ...savedSettings,
          ignoreZero: Array.isArray(savedSettings.ignoreZero) ? 
            savedSettings.ignoreZero : 
            (savedSettings.ignoreZero ? ['yes'] : ['no']),
          // Ensure new fields have defaults for backward compatibility
          useRange: savedSettings.useRange || false,
          inventory_quantity_min: savedSettings.inventory_quantity_min || '',
          inventory_quantity_max: savedSettings.inventory_quantity_max || '',
          trackQuantity: savedSettings.trackQuantity || 'unchanged',
          continueSelling: savedSettings.continueSelling || 'unchanged',
          chargeTaxes: savedSettings.chargeTaxes || 'unchanged'
        };
        setInventoryTools(updatedSettings);
        showToastMessage('Inventory settings loaded successfully', false);
      }
    } catch (error) {
      console.error('Error loading inventory settings:', error);
      showToastMessage('Failed to load inventory settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const saveInventorySettings = async () => {
    console.log('saveInventorySettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to save settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      await UserSettingsService.saveUserSettings(user.uid, 'inventory', inventoryTools);
      showToastMessage('Inventory settings saved successfully', false);
    } catch (error) {
      console.error('Error saving inventory settings:', error);
      showToastMessage('Failed to save inventory settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const showToastMessage = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setShowToast(true);
  };

  const handleInventoryToolChange = (field, value) => {
    setInventoryTools(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Return a quantity value – either fixed or random within the range.
   */
  const resolveQuantity = () => {
    if (inventoryTools.useRange) {
      const min = parseInt(inventoryTools.inventory_quantity_min) || 0;
      const max = parseInt(inventoryTools.inventory_quantity_max) || 0;
      if (min > max) return NaN;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }
    return parseInt(inventoryTools.inventory_quantity);
  };

  const handleUpdateInventory = () => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    // Ensure ignoreZero is an array and has a valid value
    const ignoreZeroArray = Array.isArray(inventoryTools.ignoreZero) ? 
      inventoryTools.ignoreZero : ['yes'];
    const shouldIgnoreZero = ignoreZeroArray.includes('yes');

    // We only need a valid quantity if we are NOT in "no changes" mode
    const hasQuantityChange = inventoryTools.useRange
      ? (inventoryTools.inventory_quantity_min !== '' || inventoryTools.inventory_quantity_max !== '')
      : inventoryTools.inventory_quantity !== '';

    const hasTrackChange = inventoryTools.trackQuantity !== 'unchanged';
    const hasContinueSellingChange = inventoryTools.continueSelling !== 'unchanged';
    const hasChargeTaxesChange = inventoryTools.chargeTaxes !== 'unchanged';

    // Require at least one change
    if (!hasQuantityChange && !hasTrackChange && !hasContinueSellingChange && !hasChargeTaxesChange) return;

    selectedProducts.forEach(product => {
      product.variants.forEach(variant => {
        // Handle both old and new edit history structures
        let currentInventory;
        
        // Safely access the edit history state
        if (editHistory?.[product.id]?.variant_updates?.[variant.id]?.inventory_quantity !== undefined) {
          currentInventory = parseInt(editHistory[product.id].variant_updates[variant.id].inventory_quantity);
        } else {
          currentInventory = variant.inventoryQuantity || variant.inventory_quantity || 0;
        }

        // Skip if ignoreZero is true and current inventory is 0
        if (shouldIgnoreZero && currentInventory === 0) {
          console.log(`Skipping variant ${variant.id} with zero inventory`);
          return;
        }

        // Get the inventory item details
        const inventoryItemId = variant.inventory_item_id || 
                               (variant.inventoryItem ? variant.inventoryItem.id : null);

        // Build the update object
        const updateObj = {
          inventory_item_id: inventoryItemId,
          old_inventory_quantity: currentInventory
        };

        // Set quantity (fixed or random)
        if (hasQuantityChange) {
          const newQuantity = resolveQuantity();
          if (!isNaN(newQuantity)) {
            updateObj.inventory_quantity = newQuantity;
          }
        }

        // Track quantity
        if (hasTrackChange) {
          updateObj.tracked = inventoryTools.trackQuantity === 'yes';
        }

        // Continue selling when out of stock → inventoryPolicy
        if (hasContinueSellingChange) {
          updateObj.inventory_policy = inventoryTools.continueSelling === 'yes' ? 'CONTINUE' : 'DENY';
        }

        // Charge taxes → taxable
        if (hasChargeTaxesChange) {
          updateObj.taxable = inventoryTools.chargeTaxes === 'yes';
        }

        updates.push({
          productId: product.id,
          variantId: variant.id,
          updates: updateObj
        });
      });
    });

    if (updates.length > 0) {
      console.log('Sending inventory updates:', updates);
      onInventoryUpdate(updates);
    }
  };

  return (
    <Card>
      {showToast && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setShowToast(false)}
        />
      )}
      
      <Card.Section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Text variant="headingLg">Inventory Tools</Text>
            <Tooltip content="Tools to help you manage product inventory">
              <Icon source={QuestionMarkMinor} color="base" />
            </Tooltip>
          </div>
          
          <ButtonGroup>
            <Button 
              icon={RefreshMinor} 
              onClick={loadInventorySettings} 
              disabled={isLoading || !user}
              tooltip="Load saved settings"
            >
              Load Settings
            </Button>
            <Button 
              icon={SaveMinor} 
              onClick={saveInventorySettings} 
              disabled={isLoading || !user}
              primary
              tooltip="Save current settings"
            >
              Save Settings
            </Button>
          </ButtonGroup>
        </div>
        
        <FormLayout>
          <FormLayout.Group>
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Set Inventory Quantity</Text>
                <Tooltip content="Set inventory quantity for all selected products. Use range mode to set a random quantity between min and max for each variant.">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <Text variant="bodyMd" color="subdued">
                Inventory tracking needs to be activated in Shopify to let this tool work
              </Text>

              {/* Toggle: Fixed vs Range */}
              <Checkbox
                label="Use random range (min – max)"
                checked={inventoryTools.useRange}
                onChange={(checked) => handleInventoryToolChange('useRange', checked)}
                helpText="When enabled, a random quantity between min and max will be chosen for each variant"
              />

              {inventoryTools.useRange ? (
                <FormLayout.Group condensed>
                  <TextField
                    label="Min Quantity"
                    type="number"
                    value={inventoryTools.inventory_quantity_min}
                    onChange={value => handleInventoryToolChange('inventory_quantity_min', value)}
                    helpText="Minimum quantity"
                    autoComplete="off"
                    min="0"
                  />
                  <TextField
                    label="Max Quantity"
                    type="number"
                    value={inventoryTools.inventory_quantity_max}
                    onChange={value => handleInventoryToolChange('inventory_quantity_max', value)}
                    helpText="Maximum quantity"
                    autoComplete="off"
                    min="0"
                  />
                </FormLayout.Group>
              ) : (
                <TextField
                  label="Set Inventory Quantity"
                  type="number"
                  value={inventoryTools.inventory_quantity}
                  onChange={value => handleInventoryToolChange('inventory_quantity', value)}
                  helpText="Set a specific inventory quantity for all selected products"
                  autoComplete="off"
                />
              )}

              {inventoryTools.useRange && inventoryTools.inventory_quantity_min !== '' && inventoryTools.inventory_quantity_max !== '' && (
                <Banner status="info">
                  <p>Each variant will get a random quantity between <strong>{inventoryTools.inventory_quantity_min}</strong> and <strong>{inventoryTools.inventory_quantity_max}</strong>.</p>
                </Banner>
              )}

              <ChoiceList
                title="Ignore products with 0 inventory"
                choices={[
                  {
                    label: 'Yes - Skip products with 0 inventory',
                    value: 'yes'
                  },
                  {
                    label: 'No - Update all products',
                    value: 'no'
                  }
                ]}
                selected={Array.isArray(inventoryTools.ignoreZero) ? inventoryTools.ignoreZero : ['yes']}
                onChange={value => handleInventoryToolChange('ignoreZero', value)}
              />

              {/* Track Quantity */}
              <div style={{ marginTop: '12px' }}>
                <Text variant="headingMd" as="h3">Track Quantity</Text>
                <Text variant="bodyMd" color="subdued">
                  Enable or disable inventory tracking for all selected variants
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Stack vertical spacing="tight">
                    <RadioButton
                      label="Don't change tracking"
                      id="track-unchanged"
                      name="trackQuantity"
                      checked={inventoryTools.trackQuantity === 'unchanged'}
                      onChange={() => handleInventoryToolChange('trackQuantity', 'unchanged')}
                    />
                    <RadioButton
                      label="Enable tracking (Track quantity)"
                      id="track-yes"
                      name="trackQuantity"
                      checked={inventoryTools.trackQuantity === 'yes'}
                      onChange={() => handleInventoryToolChange('trackQuantity', 'yes')}
                    />
                    <RadioButton
                      label="Disable tracking (Don't track quantity)"
                      id="track-no"
                      name="trackQuantity"
                      checked={inventoryTools.trackQuantity === 'no'}
                      onChange={() => handleInventoryToolChange('trackQuantity', 'no')}
                    />
                  </Stack>
                </div>
              </div>

              {/* Continue Selling When Out of Stock */}
              <div style={{ marginTop: '12px' }}>
                <Text variant="headingMd" as="h3">Continue Selling When Out of Stock</Text>
                <Text variant="bodyMd" color="subdued">
                  Control whether customers can still buy when inventory reaches 0
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Stack vertical spacing="tight">
                    <RadioButton
                      label="Don't change selling policy"
                      id="continue-unchanged"
                      name="continueSelling"
                      checked={inventoryTools.continueSelling === 'unchanged'}
                      onChange={() => handleInventoryToolChange('continueSelling', 'unchanged')}
                    />
                    <RadioButton
                      label="Yes - Continue selling when out of stock"
                      id="continue-yes"
                      name="continueSelling"
                      checked={inventoryTools.continueSelling === 'yes'}
                      onChange={() => handleInventoryToolChange('continueSelling', 'yes')}
                    />
                    <RadioButton
                      label="No - Stop selling when out of stock"
                      id="continue-no"
                      name="continueSelling"
                      checked={inventoryTools.continueSelling === 'no'}
                      onChange={() => handleInventoryToolChange('continueSelling', 'no')}
                    />
                  </Stack>
                </div>
              </div>

              {/* Charge Taxes */}
              <div style={{ marginTop: '12px' }}>
                <Text variant="headingMd" as="h3">Charge Taxes</Text>
                <Text variant="bodyMd" color="subdued">
                  Control whether taxes are charged on this product
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Stack vertical spacing="tight">
                    <RadioButton
                      label="Don't change tax setting"
                      id="tax-unchanged"
                      name="chargeTaxes"
                      checked={inventoryTools.chargeTaxes === 'unchanged'}
                      onChange={() => handleInventoryToolChange('chargeTaxes', 'unchanged')}
                    />
                    <RadioButton
                      label="Yes - Charge taxes on this product"
                      id="tax-yes"
                      name="chargeTaxes"
                      checked={inventoryTools.chargeTaxes === 'yes'}
                      onChange={() => handleInventoryToolChange('chargeTaxes', 'yes')}
                    />
                    <RadioButton
                      label="No - Don't charge taxes"
                      id="tax-no"
                      name="chargeTaxes"
                      checked={inventoryTools.chargeTaxes === 'no'}
                      onChange={() => handleInventoryToolChange('chargeTaxes', 'no')}
                    />
                  </Stack>
                </div>
              </div>

              <Button
                onClick={handleUpdateInventory}
                disabled={isInventoryUpdating}
                loading={isInventoryUpdating}
              >
                Update Inventory
              </Button>
            </Stack>
          </FormLayout.Group>
        </FormLayout>
      </Card.Section>
    </Card>
  );
}
